<template>
  <div class="wc-dialog-overlay" v-if="props.modelValue" @click.self="handleClose" @touchmove.prevent @wheel.prevent>
    <div class="cheer-record-dialog" @touchmove.stop @wheel.stop>
      <div class="wc-dialog-header">
        <img :src="cheerRecordTitle" class="login-title" />
      </div>

      <!-- Tab 切换 -->
      <div class="record-tabs flex-center">
        <div
          class="record-tab flex-center btn"
          :class="{ active: state.activeTab === 'cheer' }"
          @click="state.activeTab = 'cheer'"
        >{{ $t('wc2026.record.cheerWinTab') }}</div>
        <div
          class="record-tab flex-center btn"
          :class="{ active: state.activeTab === 'milestone' }"
          @click="state.activeTab = 'milestone'"
        >{{ $t('wc2026.record.milestoneTab') }}</div>
      </div>

      <div class="wc-dialog-body">

        <!-- 应援奖励列表 -->
        <template v-if="state.activeTab === 'cheer'">
          <div class="list-header">
            <div class="list-header-item">{{ $t('wc2026.record.matchTime') }}</div>
            <div class="list-header-item">{{ $t('wc2026.record.rewards') }}</div>
            <div class="list-header-item list-header-item--right">{{ $t('wc2026.record.cheerTime') }}</div>
          </div>
          <div class="record-list" v-if="state.cheerWinList.length > 0">
            <div class="record-item" v-for="item in state.cheerWinList" :key="item.matchId + '-' + item.cheerTime">
              <div class="record-match">
                <div class="match-time-small">{{ formatTime(item.matchTime) }}</div>
                <div class="match-name">{{ item.teamAName }} vs {{ item.teamBName }}</div>
              </div>
              <div class="record-rewards" v-if="parsedProps(item.propRewardInfo).length > 0">
                <div v-for="(r, idx) in parsedProps(item.propRewardInfo)" :key="idx" class="flex">
                  <div class="reward-item-name">{{ $t(`wc2026.item.id_${r.itemId}`) || r.itemId }}</div>
                  <div class="reward-item-count">x{{ r.quantity }}</div>
                </div>
              </div>
              <div class="record-rewards pending-rewards" v-else>—</div>
              <div class="cheer-time">
                <span class="record-time">{{ formatTime(item.cheerTime) }}</span>
              </div>
            </div>
          </div>
          <div class="empty-tip" v-else-if="!state.loading">{{ $t('wc2026.noData') }}</div>
        </template>

        <!-- 里程碑奖励列表 -->
        <template v-if="state.activeTab === 'milestone'">
          <div class="list-header">
            <div class="list-header-item">{{ $t('wc2026.record.milestone') }}</div>
            <div class="list-header-item">{{ $t('wc2026.record.rewards') }}</div>
            <div class="list-header-item list-header-item--right">{{ $t('wc2026.record.receiveTime') }}</div>
          </div>
          <div class="record-list" v-if="state.milestoneList.length > 0">
            <div class="record-item" v-for="item in state.milestoneList" :key="item.milestoneId">
              <div class="record-match">
                <div class="match-name">{{ getMilestoneTypeText(item.milestoneType, item.targetCount) }}</div>
              </div>
              <div class="record-rewards" v-if="parsedProps(item.propInfo).length > 0">
                <div v-for="(r, idx) in parsedProps(item.propInfo)"  :key="idx" class="flex-center">
                  <div class="reward-item-name">{{ $t(`wc2026.item.id_${r.propId}`) || r.propId }}</div>
                  <div class="reward-item-count">x{{ r.propCount }}</div>
                </div>
              </div>
              <div class="record-rewards" v-else>—</div>
              <div class="cheer-time">
                <span class="record-time">{{ formatTime(item.receiveTime) }}</span>
              </div>
            </div>
          </div>
          <div class="empty-tip" v-else-if="!state.loading">{{ $t('wc2026.noData') }}</div>
        </template>

      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable */
import { reactive, defineProps, defineEmits, watch, computed, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import WcApi from '@/config/wcActApi'
import cheerRecordTitleVi from "@/assets/imgs/act/wc2026/cheerRecordDialog/titlevi.png";
import cheerRecordTitleTh from "@/assets/imgs/act/wc2026/cheerRecordDialog/titleth.png";
import cheerRecordTitleId from "@/assets/imgs/act/wc2026/cheerRecordDialog/titleid.png";
import cheerRecordTitleZh from "@/assets/imgs/act/wc2026/cheerRecordDialog/titlezh.png";
import cheerRecordTitleEn from "@/assets/imgs/act/wc2026/cheerRecordDialog/titleen.png";
import { formatInTimezone } from "../../utils/time";

const props = defineProps({
  modelValue: { type: Boolean, default: false },
})

// 禁止/恢复页面滚动
const lockScroll = () => {
  document.body.classList.add('no-scroll')
  document.documentElement.classList.add('no-scroll')
}

const unlockScroll = () => {
  document.body.classList.remove('no-scroll')
  document.documentElement.classList.remove('no-scroll')
}

const emit = defineEmits(['update:modelValue'])
const { t, locale } = useI18n()

const cheerRecordTitle = computed(() => {
  const currentLocale = locale.value;
  switch (currentLocale) {
    case "vi": return cheerRecordTitleVi;
    case "th": return cheerRecordTitleTh;
    case "id": return cheerRecordTitleId;
    case "en": return cheerRecordTitleEn;
    case "tw":
    case "hk": return cheerRecordTitleZh;
    default:   return cheerRecordTitleEn;
  }
});

const state = reactive({
  loading: false,
  activeTab: 'cheer',
  cheerWinList: [],
  milestoneList: [],
})

const handleClose = () => emit('update:modelValue', false)

const formatTime = (ts) => formatInTimezone(ts, locale.value, 'MM/DD HH:mm')

const parsedProps = (raw) => {
  if (!raw) return []
  try {
    return typeof raw === 'string' ? JSON.parse(raw) : raw
  } catch {
    return []
  }
}

const getMilestoneTypeText = (type, count) => {
  return `${t(`wc2026.milestone.title${type}`).replace('_N_', count)}`
}

const loadRecords = async () => {
  state.loading = true
  try {
    const res = await WcApi.getRewardRecord()
    state.cheerWinList = res.cheerWinList || []
    state.milestoneList = res.milestoneList || []
  } catch (e) {
    console.error(e)
  } finally {
    state.loading = false
  }
}

watch(() => props.modelValue, (val) => {
  if (val) {
    lockScroll()
    state.activeTab = 'cheer'
    loadRecords()
  } else {
    unlockScroll()
  }
}, { immediate: true })

onUnmounted(() => {
  unlockScroll()
})
</script>

<style lang="less" scoped>
@import '../shared.less';

.cheer-record-dialog {
  width: 673px;
  height: 480px;
  background-image: url("~@/assets/imgs/act/wc2026/dialog/bg.png");
  font-size: 15px;
  font-weight: bold;
  line-height: 1.5;
  color: #1B0926;
  padding: 0 30px;

  .wc-dialog-body {
    overflow-y: auto;
    flex: 1;
  }
}

.login-title {
  height: 41px;
  width: auto;
  display: block;
  margin: 18px auto 12px;
}

.list-header {
  display: flex;
  gap: 10px;
  padding: 0 30px;
  padding-bottom: 10px;
  border-bottom: 1px solid #50CA69;

  .list-header-item {
    flex: 1;
    &--right {
      text-align: right;
    }
  }
}

.record-list {
  padding: 10px 20px 0;
  height: 270px;
  overflow-y: auto;

  .record-item {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 20px;

    .record-match {
      flex: 1;
      width: 30%;
    }
    .record-rewards {

      width: 50%;
      text-align: left;
    }
    .cheer-time {
      flex: 1;
      width: 20%;
      text-align: right;
    }
  }
}

.empty-tip {
  text-align: center;
  margin-top: 20px;
}
.record-tabs {
  gap: 60px;
  .record-tab {
    width: 256px;
    height: 89px;
    background-image: url("~@/assets/imgs/act/wc2026/btn-large.png");
    background-size: 100% 100%;
    background-repeat: no-repeat;
    background-position: center;
    &.active {
      background-image: url("~@/assets/imgs/act/wc2026/btn-large-active.png");
    }
  }
}
</style>
